/**
 * Heart doodles — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_doodles`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const heartDoodlesPack: ToilePack = {
    slug: 'heart-doodles',
    category: 'coloring',
    name: 'Heart doodles',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'patterned', label: 'Patterned hearts', subject: 'sixteen hearts, each filled with its own pattern of stripes, dots, checks or tiny flowers'},
        {key: 'winged', label: 'Hearts with extras', subject: 'hearts with wings, a heart with an arrow through it, a heart balloon on a string and a heart padlock'},
        {key: 'chains', label: 'Heart chains', subject: 'heart borders, a garland of hearts on a string, a row of linked hearts and a heart vine, stacked across the page'},
    ],
    styles: [
        {key: 'fineliner', label: 'Fineliner', style: 'clean fineliner pen, thin even black lines, closed shapes and small details drawn with care', palette: 'Black ink only, no gray tones.', framing: 'The drawings spread across the whole sheet with wide white gaps between them.'},
        {key: 'brush', label: 'Brush pen', style: 'bold brush-pen strokes, thick-to-thin tapering lines and a playful hand-drawn wobble', palette: 'Black ink only, no gray tones.', framing: 'The drawings spread across the whole sheet with wide white gaps between them.'},
        {key: 'pastel', label: 'Pastel fill', style: 'black pen outlines with loose scribbled marker fills', palette: 'Soft pink, coral and lilac fills inside the black outlines, the rest of the sheet white.', framing: 'The drawings spread across the whole sheet with wide white gaps between them.'},
    ],
    directions: ['with stars', 'red ink', 'big hearts'],
    directionHint: 'try: with stars',
    seo: {
        title: 'Heart Doodles: Patterned, Winged, Chained Hearts',
        metaDescription: 'Nine sheets of heart doodles drawn a dozen ways, patterned hearts, hearts with wings and heart chains, for cards and gift tags.',
        h1: 'Heart doodles: patterned hearts, hearts with extras, heart chains',
        tagline: 'Hearts in fineliner, brush pen and soft pastel fill, spaced across the sheet.',
        intro: 'Heart doodles are hearts drawn many ways, for cards, letters, journal pages and gift tags. These nine pages are sheets of heart doodles spaced across the sheet with wide white gaps: patterned hearts, hearts with extras, and heart chains. Each sheet is drawn in fineliner, brush pen or with soft pink, coral and lilac fills. For a different variant, type it in the direction box.',
        useCases: [
            'The inside of a greeting card',
            'A letter or note decoration',
            'A gift tag',
            'A journal page',
        ],
        faq: [
            {q: 'Can I print these for a card?', a: 'Yes. Each sheet is black line on a white page, so it prints cleanly and can be cut down to the size of a card.'},
            {q: 'What does the pastel style look like?', a: 'Black pen outlines with loose, scribbled marker fills in soft pink, coral and lilac, and the rest of the sheet white.'},
            {q: 'Can I request a different variant?', a: 'Yes. Type it in the direction box, such as with stars or big hearts. A typed request may differ from the samples.'},
        ],
    },
}
